/* eslint formatjs/no-literal-string-in-jsx: off -- internal tool, intentionally not translated */
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  JsonEditor,
  type CustomKeyProps,
  type CustomNodeDefinition,
  type CustomNodeProps,
  type FilterFunction,
  type JsonData,
  type NodeData,
  type ThemeStyles
} from "json-edit-react"
import { useDispatch, useSelector } from "react-redux"
import { useIntl } from "react-intl"
import {
  Alert,
  Box,
  Button,
  ClickAwayListener,
  CloseIcon,
  IconButton,
  InfoIcon,
  Progress,
  Tooltip,
  Typography,
  useTheme
} from "@carrotfertility/carotene-core"
import { getIsDynamicContentToggleToolOpen } from "#/redux/reducers/application"
import { toggleDymamicContentTogglesTool } from "#/redux/actions/testUserActions"
import { RootState } from "#/redux/reducers"
import { useCurrentUser } from "../context/user/UserContext"
import { useLocaleContext } from "#/components/context/user/LocaleContext"
import { Locale } from "#/types/Locale"
import { localeToggleOptions } from "./localeToggleOptions"
import { resetHelpCenterPreview, setHelpCenterPreview } from "#/services/help-center/helpCenterPreviewStore"
import { useHelpCenterCategories } from "#/services/help-center/useGetHelpCenter"
import { useMemberContext } from "#/services/help-center/useMemberContext"
import {
  buildDateFields,
  buildEnumOptions,
  buildFieldDescriptions,
  buildFieldLabels,
  buildNullableEnumPaths,
  buildScalarKinds,
  useMemberContextSchema,
  type DateFormat,
  type EnumOption,
  type ScalarKind
} from "#/services/help-center/memberContextSchema"

const DEBOUNCE_MS = 400

// Gives the user time to move the mouse from the icon onto the tooltip body before it closes, so links are clickable.
const TOOLTIP_HIDE_DELAY_MS = 200

type FieldNodeProps = {
  labels: Map<string, string>
  descriptions: Map<string, string>
  changedPaths: Set<string>
  originalBody: JsonData | null
}
type EnumNodeProps = FieldNodeProps & { options: Map<string, EnumOption[]>; nullableEnums: Set<string> }
type DateNodeProps = FieldNodeProps & { formats: Map<string, DateFormat> }

// Wrapping inputs (select/date/number) sit on their own line under the label. Unlike checkboxes, these rows fill
// their flex line, so they need explicit block margins to keep from crowding their own label (above) and the next
// field's label (below). The max width keeps them from overflowing the narrow tool panel.
const fieldControlStyle: React.CSSProperties = {
  boxSizing: "border-box",
  display: "block",
  minWidth: 0,
  maxWidth: "min(100%, 15rem)",
  marginBlockStart: "0.25rem",
  marginBlockEnd: "0.5rem"
}

function valueAtPath(root: JsonData | null, path: (string | number)[]): unknown {
  let current: unknown = root
  for (const key of path) {
    if (current === null || typeof current !== "object") return undefined
    current = (current as Record<string, unknown>)[String(key)]
  }
  return current
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
}

// Walks edited vs. original, recording every changed leaf path AND its ancestor paths, so a collapsed parent
// section is flagged when something inside it differs from the member's context. Returns whether `current` changed.
function collectChangedPaths(current: unknown, original: unknown, path: string[], out: Set<string>): boolean {
  let changed: boolean
  if (isPlainObject(current) && isPlainObject(original)) {
    changed = false
    for (const key of new Set([...Object.keys(current), ...Object.keys(original)])) {
      if (collectChangedPaths(current[key], original[key], [...path, key], out)) changed = true
    }
  } else {
    changed = JSON.stringify(current ?? null) !== JSON.stringify(original ?? null)
  }
  if (changed && path.length > 0) out.add(path.join("."))
  return changed
}

// Objects, arrays, null values, and items inside arrays can't be edited as raw JSON.
// typeof null === "object" in JS, so this covers null implicitly.
const restrictCollectionEdit: FilterFunction = (nodeData) =>
  typeof nodeData.value === "object" || Array.isArray(nodeData.parentData)

// Only http(s) and root-relative links are allowed; anything else (e.g. javascript:) renders as plain text.
const isSafeHref = (href: string): boolean => /^(https?:\/\/|\/)/i.test(href.trim())

// Renders a string containing markdown-style `[text](url)` links as a mix of plain text and anchor elements.
// Anything that isn't a (safe) link is passed through verbatim. (Ported from the carrot-contentful-app query editor.)
function renderWithMarkdownLinks(text: string): React.ReactNode[] {
  const pattern = /\[([^\]]+)\]\(([^)]+)\)/g
  const nodes: React.ReactNode[] = []
  let cursor = 0
  for (const match of text.matchAll(pattern)) {
    const [fullMatch, linkText, href] = match
    const start = match.index ?? 0
    if (start > cursor) nodes.push(text.slice(cursor, start))
    nodes.push(
      isSafeHref(href) ? (
        <a
          key={`${start}-${href}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "inherit", textDecoration: "underline" }}
        >
          {linkText}
        </a>
      ) : (
        linkText
      )
    )
    cursor = start + fullMatch.length
  }
  if (cursor < text.length) nodes.push(text.slice(cursor))
  return nodes
}

// An info icon whose schema description (with markdown links) shows on hover/focus/click. The Tooltip's `open` state
// is controlled by our own handlers rather than MUI's built-in hover: json-edit-react owns the key row's click/ref
// wiring, which breaks MUI's cloneElement-based event injection. A close delay plus mouse handlers on the tooltip body
// let the cursor travel onto the tooltip to click links. stopPropagation keeps clicking the icon from collapsing the node.
function FieldInfoTooltip({ description }: { description: string }): JSX.Element {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>()
  const cancelClose = (): void => clearTimeout(closeTimer.current)
  const scheduleClose = (): void => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpen(false), TOOLTIP_HIDE_DELAY_MS)
  }
  useEffect(() => () => cancelClose(), [])

  const title = (
    <Box onMouseEnter={cancelClose} onMouseLeave={scheduleClose}>
      {renderWithMarkdownLinks(description)}
    </Box>
  )

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip title={title} placement="right" open={open} onClose={() => setOpen(false)}>
        <IconButton
          aria-label="Field description"
          size="small"
          onMouseEnter={() => {
            cancelClose()
            setOpen(true)
          }}
          onMouseLeave={scheduleClose}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={(event) => {
            event.stopPropagation()
            setOpen((prev) => !prev)
          }}
          sx={{ padding: 0, marginInlineStart: "0.3em", verticalAlign: "middle" }}
        >
          <InfoIcon sx={{ fontSize: "0.9em", color: "text.secondary" }} />
        </IconButton>
      </Tooltip>
    </ClickAwayListener>
  )
}

// Renders a field's key as its schema display name (with a separating colon) instead of the raw property name.
// Changed fields are tinted via the editor theme's `property` color, so no per-label marker is needed here.
function FieldLabelKey({ name, path, styles, customNodeProps }: CustomKeyProps<FieldNodeProps>): JSX.Element {
  const label = customNodeProps?.labels.get(path.join(".")) ?? String(name)
  const description = customNodeProps?.descriptions.get(path.join("."))
  return (
    <span style={styles}>
      {label}:{description && <FieldInfoTooltip description={description} />}
    </span>
  )
}

// Wraps a value control, appending a per-field Reset that restores just this field to the member's value
// (shown only while the field differs).
function ValueControl({
  nodeData,
  handleEdit,
  customNodeProps,
  children
}: {
  nodeData: NodeData
  handleEdit: (value?: unknown) => void
  customNodeProps?: FieldNodeProps
  children: React.ReactNode
}): JSX.Element {
  const changed = customNodeProps?.changedPaths.has(nodeData.path.join(".")) ?? false
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
      {children}
      {changed && (
        <Button
          variant="text"
          size="small"
          onClick={() => handleEdit(valueAtPath(customNodeProps?.originalBody ?? null, nodeData.path))}
          sx={{ minInlineSize: 0, paddingBlock: 0, paddingInline: "0.25rem" }}
        >
          Reset
        </Button>
      )}
    </span>
  )
}

function EnumSelectNode({
  value,
  setValue,
  handleEdit,
  nodeData,
  customNodeProps
}: CustomNodeProps<EnumNodeProps>): JSX.Element {
  const path = nodeData.path.join(".")
  const options = customNodeProps?.options.get(path) ?? []
  const isNullable = customNodeProps?.nullableEnums.has(path) ?? false
  // Options carry stringified values, so coerce here to keep numeric enum values matching their option.
  const current = value == null ? "" : String(value)
  const hasCurrent = options.some((option) => option.value === current)
  return (
    <ValueControl nodeData={nodeData} handleEdit={handleEdit} customNodeProps={customNodeProps}>
      <select
        style={fieldControlStyle}
        value={current}
        onChange={(event) => {
          const next = event.target.value || null
          setValue(next)
          handleEdit(next)
        }}
      >
        {isNullable && <option value="">(unset)</option>}
        {!hasCurrent && current !== "" && <option value={current}>{current}</option>}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </ValueControl>
  )
}

// Convert a stored UTC instant to the local "YYYY-MM-DDTHH:mm" a datetime-local input expects, so the displayed
// value and the local->UTC conversion in onChange agree (otherwise the instant shifts by the timezone offset).
function toDateTimeLocalValue(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ""
  const pad = (n: number): string => String(n).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes()
  )}`
}

function DateInputNode({
  value,
  setValue,
  handleEdit,
  nodeData,
  customNodeProps
}: CustomNodeProps<DateNodeProps>): JSX.Element {
  const format = customNodeProps?.formats.get(nodeData.path.join(".")) ?? "date"
  const raw = typeof value === "string" ? value : ""
  const control =
    format === "date-time" ? (
      <input
        type="datetime-local"
        style={fieldControlStyle}
        value={toDateTimeLocalValue(raw)}
        onChange={(event) => {
          const next = event.target.value ? new Date(event.target.value).toISOString() : null
          setValue(next)
          handleEdit(next)
        }}
      />
    ) : (
      <input
        type="date"
        style={fieldControlStyle}
        value={raw.slice(0, 10)}
        onChange={(event) => {
          const next = event.target.value || null
          setValue(next)
          handleEdit(next)
        }}
      />
    )
  return (
    <ValueControl nodeData={nodeData} handleEdit={handleEdit} customNodeProps={customNodeProps}>
      {control}
    </ValueControl>
  )
}

function NumberInputNode({
  value,
  setValue,
  handleEdit,
  nodeData,
  customNodeProps
}: CustomNodeProps<FieldNodeProps>): JSX.Element {
  const current = typeof value === "number" ? String(value) : ""
  return (
    <ValueControl nodeData={nodeData} handleEdit={handleEdit} customNodeProps={customNodeProps}>
      <input
        type="number"
        style={fieldControlStyle}
        value={current}
        onChange={(event) => {
          const next = event.target.value
          if (next === "") {
            setValue(null)
            handleEdit(null)
          } else if (!Number.isNaN(Number(next))) {
            setValue(Number(next))
            handleEdit(Number(next))
          }
        }}
      />
    </ValueControl>
  )
}

// Non-nullable boolean fields — checkbox only, no null state.
function BooleanCheckboxNode({
  value,
  setValue,
  handleEdit,
  nodeData,
  customNodeProps
}: CustomNodeProps<FieldNodeProps>): JSX.Element {
  return (
    <ValueControl nodeData={nodeData} handleEdit={handleEdit} customNodeProps={customNodeProps}>
      <input
        type="checkbox"
        checked={value === true}
        onChange={(event) => {
          setValue(event.target.checked)
          handleEdit(event.target.checked)
        }}
      />
    </ValueControl>
  )
}

// Nullable boolean fields — three-option select: (unset) / Yes / No.
function NullableBooleanSelectNode({
  value,
  setValue,
  handleEdit,
  nodeData,
  customNodeProps
}: CustomNodeProps<FieldNodeProps>): JSX.Element {
  const current = value === true ? "true" : value === false ? "false" : ""
  return (
    <ValueControl nodeData={nodeData} handleEdit={handleEdit} customNodeProps={customNodeProps}>
      <select
        style={fieldControlStyle}
        value={current}
        onChange={(event) => {
          const next = event.target.value === "true" ? true : event.target.value === "false" ? false : null
          setValue(next)
          handleEdit(next)
        }}
      >
        <option value="">(unset)</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    </ValueControl>
  )
}

function LocaleToggle(): JSX.Element {
  const { locale } = useIntl()
  const { setLocale } = useLocaleContext()
  return (
    <Box component="label" sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
      <Typography variant="caption" color="text.secondary">
        {"Locale"}
      </Typography>
      <select
        style={{ ...fieldControlStyle, display: "inline-block", marginBlock: 0 }}
        value={locale}
        onChange={(event) => setLocale(Locale.getLocaleFromString(event.target.value))}
      >
        {localeToggleOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Box>
  )
}

export const JsonConditionsToggles: FC = () => {
  const currentUser = useCurrentUser()
  if (!currentUser?.isTestUser) {
    return null
  }
  return <RenderedJsonConditionsToggles />
}

const RenderedJsonConditionsToggles: FC = () => {
  const dispatch = useDispatch()
  const muiTheme = useTheme()
  const isOpen = useSelector((state: RootState) => getIsDynamicContentToggleToolOpen(state))

  const { data: memberContext } = useMemberContext(isOpen)
  const { isError, error } = useHelpCenterCategories()
  const { data: schema } = useMemberContextSchema()

  const enumOptions = useMemo(() => (schema ? buildEnumOptions(schema) : new Map<string, EnumOption[]>()), [schema])
  const nullableEnums = useMemo(() => (schema ? buildNullableEnumPaths(schema) : new Set<string>()), [schema])
  const dateFields = useMemo(() => (schema ? buildDateFields(schema) : new Map<string, DateFormat>()), [schema])
  const scalarKinds = useMemo(() => (schema ? buildScalarKinds(schema) : new Map<string, ScalarKind>()), [schema])
  const fieldLabels = useMemo(() => (schema ? buildFieldLabels(schema) : new Map<string, string>()), [schema])
  const fieldDescriptions = useMemo(
    () => (schema ? buildFieldDescriptions(schema) : new Map<string, string>()),
    [schema]
  )

  // The member's resolved context body, as seeded — the baseline that edits are compared and reset against.
  const originalBody = useMemo<JsonData | null>(
    () => (memberContext as { body?: JsonData } | undefined)?.body ?? null,
    [memberContext]
  )

  // Seed the editor from the member's context once it loads. Edits replace `editorData` immutably (json-edit-react
  // never mutates in place), so `originalBody` stays the pristine baseline for change detection and reset.
  const [editorData, setEditorData] = useState<JsonData | null>(null)
  useEffect(() => {
    if (editorData === null && originalBody !== null) {
      setEditorData(originalBody)
    }
  }, [originalBody, editorData])

  // Dotted paths of every changed field plus its ancestor sections, so parents flag changes nested within them.
  const changedPaths = useMemo(() => {
    const paths = new Set<string>()
    if (editorData !== null && originalBody !== null) collectChangedPaths(editorData, originalBody, [], paths)
    return paths
  }, [editorData, originalBody])
  const hasChanges = changedPaths.size > 0

  const [filter, setFilter] = useState("")
  const filterText = filter.trim().toLowerCase()

  // Match the schema display label the user sees (falling back to the raw key) plus the dotted path, so typing a
  // section name like "member" narrows to that subtree. json-edit-react keeps ancestors of matches visible.
  const matchesFilter = useCallback(
    (path: (string | number)[], key: string | number): boolean => {
      const pathStr = path.join(".")
      const label = fieldLabels.get(pathStr) ?? String(key)
      return `${pathStr} ${label}`.toLowerCase().includes(filterText)
    },
    [fieldLabels, filterText]
  )

  const hasFilterMatches = useMemo(() => {
    if (filterText === "" || editorData === null) return true
    let found = false
    const walk = (value: unknown, path: string[]): void => {
      if (found) return
      if (path.length > 0 && matchesFilter(path, path[path.length - 1])) {
        found = true
        return
      }
      if (value !== null && typeof value === "object") {
        for (const [key, child] of Object.entries(value)) walk(child, [...path, key])
      }
    }
    walk(editorData, [])
    return found
  }, [filterText, editorData, matchesFilter])

  // Render as a labeled form, not raw JSON: hide braces, bold field names, space out rows, and tint changed fields.
  const editorTheme = useMemo<Partial<ThemeStyles>>(() => {
    const changedColor = (nodeData: NodeData): React.CSSProperties | null => {
      // The root has an empty path; flag it whenever anything has changed.
      const flagged = nodeData.path.length === 0 ? changedPaths.size > 0 : changedPaths.has(nodeData.path.join("."))
      return flagged ? { color: muiTheme.palette.primary.main } : null
    }
    return {
      container: { fontFamily: muiTheme.typography.fontFamily, minWidth: 0 },
      collectionElement: { marginBlockEnd: muiTheme.spacing(1.5) },
      bracket: { display: "none" },
      iconCopy: { display: "none" },
      property: [{ fontWeight: 600, color: muiTheme.palette.text.primary }, changedColor]
    }
  }, [muiTheme, changedPaths])

  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  useEffect(() => () => clearTimeout(debounceRef.current), [])

  // Toggle preview with the tool. On close, also clear the editor, filter, and any pending debounce so reopening
  // starts fresh from the member's context (re-seeded below) — matching the cleared overrides — instead of stale edits.
  useEffect(() => {
    if (isOpen) {
      setHelpCenterPreview({ active: true })
    } else {
      resetHelpCenterPreview()
      clearTimeout(debounceRef.current)
      setEditorData(null)
      setFilter("")
    }
    return () => resetHelpCenterPreview()
  }, [isOpen])

  function onEditorChange(next: JsonData): void {
    setEditorData(next)
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(
      () => setHelpCenterPreview({ memberContextOverrides: { body: next } }),
      DEBOUNCE_MS
    )
  }

  function onReset(): void {
    clearTimeout(debounceRef.current)
    setEditorData(originalBody)
    setHelpCenterPreview({ memberContextOverrides: null })
  }

  const customNodeDefinitions: CustomNodeDefinition[] = useMemo(() => {
    const customKey = FieldLabelKey as CustomNodeDefinition["customKey"]
    const baseProps = { labels: fieldLabels, descriptions: fieldDescriptions, changedPaths, originalBody }
    const valueNode = { customKey, showOnView: true, showOnEdit: true, showEditTools: false }
    return [
      {
        ...valueNode,
        condition: (nodeData) => enumOptions.has(nodeData.path.join(".")),
        element: EnumSelectNode as CustomNodeDefinition["element"],
        customNodeProps: { ...baseProps, options: enumOptions, nullableEnums }
      },
      {
        ...valueNode,
        condition: (nodeData) => dateFields.has(nodeData.path.join(".")),
        element: DateInputNode as CustomNodeDefinition["element"],
        customNodeProps: { ...baseProps, formats: dateFields }
      },
      {
        ...valueNode,
        condition: (nodeData) => scalarKinds.get(nodeData.path.join(".")) === "number",
        element: NumberInputNode as CustomNodeDefinition["element"],
        customNodeProps: baseProps
      },
      {
        ...valueNode,
        condition: (nodeData) => scalarKinds.get(nodeData.path.join(".")) === "nullable-boolean",
        element: NullableBooleanSelectNode as CustomNodeDefinition["element"],
        customNodeProps: baseProps
      },
      {
        ...valueNode,
        condition: (nodeData) => scalarKinds.get(nodeData.path.join(".")) === "boolean",
        element: BooleanCheckboxNode as CustomNodeDefinition["element"],
        customNodeProps: baseProps
      },
      {
        condition: (nodeData) => fieldLabels.has(nodeData.path.join(".")),
        customKey,
        customNodeProps: baseProps
      }
    ]
  }, [enumOptions, nullableEnums, dateFields, scalarKinds, fieldLabels, fieldDescriptions, changedPaths, originalBody])

  if (!isOpen) {
    return null
  }

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        insetBlockStart: theme.spacing(theme.tokens.spacing.md),
        insetInlineStart: theme.spacing(theme.tokens.spacing.md),
        zIndex: theme.zIndex.modal,
        inlineSize: "28rem",
        maxBlockSize: "calc(100vh - 2rem)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        padding: theme.spacing(theme.tokens.spacing.md),
        borderRadius: theme.tokens.borderRadius.sm,
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[3]
      })}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6">Dynamic Content Tool (JSON Conditions)</Typography>
        <IconButton aria-label="Close" onClick={() => dispatch(toggleDymamicContentTogglesTool())}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ marginBlockStart: "0.5rem" }}>
        <LocaleToggle />
      </Box>

      <Box sx={{ marginBlockStart: "0.5rem" }}>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter fields…"
          aria-label="Filter fields"
          style={{ boxSizing: "border-box", inlineSize: "100%" }}
        />
      </Box>

      <Box
        sx={{
          marginBlockStart: "0.5rem",
          minBlockSize: "1.75rem",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap"
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {"Fields you change are "}
          <Box component="span" sx={{ color: "primary.main", fontWeight: 600 }}>
            {"highlighted"}
          </Box>
        </Typography>
        {hasChanges && (
          <Button
            variant="text"
            size="small"
            onClick={onReset}
            sx={{ minInlineSize: 0, paddingBlock: 0, paddingInline: "0.25rem" }}
          >
            {"Reset all"}
          </Button>
        )}
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ marginBlockStart: "0.5rem" }}>
          Preview failed{error?.statusCode ? ` (HTTP ${error.statusCode})` : ""}. The edited context may be invalid for
          the schema.
        </Alert>
      ) : editorData === null ? (
        <Box sx={{ marginBlockStart: "0.5rem", display: "flex", justifyContent: "center" }}>
          <Progress aria-label="Loading member context" />
        </Box>
      ) : (
        <Box sx={{ marginBlockStart: "0.5rem", flex: 1, minBlockSize: 0, overflowY: "auto", overflowX: "hidden" }}>
          {filterText !== "" && !hasFilterMatches ? (
            <Typography variant="body2" color="text.secondary">
              {`No fields match "${filter}".`}
            </Typography>
          ) : (
            <JsonEditor
              data={editorData}
              setData={onEditorChange}
              restrictAdd
              restrictDelete
              restrictEdit={restrictCollectionEdit}
              restrictTypeSelection
              showStringQuotes={false}
              showCollectionCount={false}
              indent={2}
              theme={editorTheme}
              customNodeDefinitions={customNodeDefinitions}
              rootName="Content Member Context"
              collapse={2}
              searchText={filter}
              searchFilter={(nodeData) => matchesFilter(nodeData.path, nodeData.key)}
            />
          )}
        </Box>
      )}
    </Box>
  )
}
